import React, { Fragment, useCallback, useState, useEffect } from 'react';
import { Transition, Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

/**
 * Share sheet for a show: copy link, email, SMS — plus native share when available.
 */
function ShareShowModal({ isOpen, onClose, showId, showTitle }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) setCopied(false);
  }, [isOpen]);

  const shareUrl =
    typeof window !== 'undefined' && showId != null
      ? `${window.location.origin}/show/${showId}`
      : '';

  const title = showTitle || 'Art show';

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error(e);
    }
  }, [shareUrl]);

  const mailtoHref = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(
    `${title}\n\n${shareUrl}`
  )}`;

  const smsHref = `sms:?&body=${encodeURIComponent(`${title} ${shareUrl}`)}`;

  const handleNativeShare = useCallback(async () => {
    if (!navigator.share || !shareUrl) return;
    try {
      await navigator.share({
        title,
        text: title,
        url: shareUrl,
      });
      onClose();
    } catch (e) {
      if (e.name !== 'AbortError') console.error(e);
    }
  }, [shareUrl, title, onClose]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto p-4 flex items-end sm:items-center justify-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-t-2xl sm:rounded-2xl bg-white p-0 shadow-xl font-oswald">
              <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                <Dialog.Title className="text-lg font-semibold text-gray-900">Share show</Dialog.Title>
                <button
                  type="button"
                  className="rounded-full p-1 text-gray-500 hover:bg-gray-100"
                  onClick={onClose}
                  aria-label="Close"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <ul className="py-2 text-base">
                {typeof navigator !== 'undefined' && navigator.share && (
                  <li>
                    <button
                      type="button"
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                      onClick={handleNativeShare}
                    >
                      Share…
                    </button>
                  </li>
                )}
                <li>
                  <button
                    type="button"
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                    onClick={handleCopy}
                  >
                    {copied ? 'Link copied' : 'Copy link'}
                  </button>
                </li>
                <li>
                  <a
                    href={mailtoHref}
                    className="block px-4 py-3 hover:bg-gray-50 transition-colors"
                    onClick={onClose}
                  >
                    Email
                  </a>
                </li>
                <li>
                  <a
                    href={smsHref}
                    className="block px-4 py-3 hover:bg-gray-50 transition-colors"
                    onClick={onClose}
                  >
                    Message
                  </a>
                </li>
              </ul>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

export default ShareShowModal;
